/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { get, isUndefined } from 'lodash';
import { useContext } from 'react';
import { Badge, Button, Callout, CopyButton, Disclosure, Link, Text } from '@cimpress-ui/react';
import { ShipmentPlanViewer } from '@cimpress-technology/shipment-planner-component';
import { InvalidConsideredOption, Option } from '../../types';
import { AccessContext } from '../DecisionViewer';
import { FormattedAlertMessages } from './FormattedAlertMessages';
import { RetryContext } from '../Retry/RetryContext';
import { RoutingReceiptSummary } from './RoutingReceiptSummary';
import { Rank } from './Rank';
import { FulfillmentOptionHeaderActions } from './FulfillmentOptionHeaderActions';

const fulfillmentOptionBodyStyles = css`
  margin-bottom: 10px;
  container-type: inline-size;

  h3 {
    font-size: 13px;
    font-weight: 700;
    line-height: 17.7px;
    margin-bottom: 8px;
    margin-top: 0;
  }

  p {
    font-size: 13px;
    line-height: 17.7px;
  }
`;

const noShipmentPlanStyles = css`
  h3 {
    font-size: 1.3rem;
    font-weight: 700;
    line-height: 17.7px;
    margin-bottom: 0.8rem;
    margin-top: 0;
  }
`;

const fgMuted = '#94979b';
const fgInverse = '#fff';
const bgAccent = '#0281a1';

export const FulfillerAndPrice = ({
  option,
  halfWeight,
}: {
  option: Option | InvalidConsideredOption;
  halfWeight?: boolean;
}) => {
  const labelStyle = {
    color: fgInverse,
    border: 'none',
    fontSize: halfWeight ? '0.8rem' : '1.2rem',
    alignSelf: 'center',
  };

  const fullfillerStyle = {
    ...labelStyle,
    backgroundColor: bgAccent,
  };

  const priceStyle = {
    ...labelStyle,
    backgroundColor: fgMuted,
  };

  let leftOffset = '300px';
  if (!isUndefined(option.idpResult)) {
    leftOffset = halfWeight ? '450px' : '400px';
  } else {
    leftOffset = halfWeight ? '350px' : '300px';
  }

  const accordionHeaderStyles = css`
    position: absolute;
    left: ${leftOffset};

    display: grid;
    grid-template-columns: 30px 1fr;
    align-items: center;
    gap: 1.6rem;

    // This will not affect the way it looks,
    // but it does affect whether it covers up stuff at the end
    // of the disclosure
    width: 50%;

    @container (max-width: 600px) {
      gap: 0.8rem;
    }

    .labelWrapper {
      display: flex;
      gap: 1.8rem;
      margin: 0.8rem;

      @container (max-width: 340px) {
        flex-wrap: wrap;
      }
    }
  `;

  return (
    <div css={accordionHeaderStyles}>
      <CopyButton
        value={option._links?.productConfiguration?.href || ''}
        isDisabled={!option._links?.productConfiguration?.href}
        variant="tertiary"
      />
      <div className="labelWrapper">
        {(option.fulfillerName || option.fulfillerId) && (
          <Badge UNSAFE_style={fullfillerStyle} size="large">
            {option.fulfillerName || option.fulfillerId || ''}
          </Badge>
        )}
        {option.totalCostUSD && option.totalCostUSD > 0 && (
          <Badge UNSAFE_style={priceStyle} size="large">
            {'$' + Number(Math.round(option.totalCostUSD * 100) / 100).toFixed(2)}
          </Badge>
        )}
      </div>
    </div>
  );
};

const headlineGrid = css`
  display: grid;
  grid-template-columns: 24% 1fr;
  gap: 0 1.6rem;
  line-height: 17.7px;
  font-size: 1.3rem;
  grid-column: 1 / span 2;

  .pickMeButton {
    justify-self: flex-end;
  }

  @container (max-width: 625px) {
    max-width: 100%;
    grid-column: 1;
  }
`;

export const FulfillmentOption = ({
  option,
  isItemSuspendedInRouting,
  halfWeight,
}: {
  option: Option | InvalidConsideredOption | undefined;
  isItemSuspendedInRouting: boolean;
  halfWeight?: boolean;
}) => {
  const headerStyles = css`
    line-height: 17.7px;
    font-size: ${halfWeight ? '0.8rem' : '1.3rem'};
    text-transform: uppercase;
    color: #0000006e;
    font-weight: 600;
    word-break: break-word;
  `;

  const productInfoGridStyles = css`
    display: grid;
    grid-template-columns: ${halfWeight ? '27% 1fr' : '24% 1fr'};
    gap: ${halfWeight ? '0 1.3rem' : '0 1.6rem'};
    line-height: 19px;
    font-size: ${halfWeight ? '0.9rem' : '1.3rem'};

    @container (max-width: 800px) {
      gap: ${halfWeight ? '0.4rem 1.3rem' : '0.8rem 1.6rem'};
      align-items: center;
    }

    @container (max-width: 625px) {
      grid-template-columns: 100%;
      margin-bottom: ${halfWeight ? '1.3rem' : '1.6rem'};
      gap: 0;

      > div:not:first-child {
        max-width: ${halfWeight ? 'calc(100% - 1.3rem)' : 'calc(100% - 1.6rem)'};
      }

      > div:nth-of-type(odd) {
        margin-bottom: ${halfWeight ? '1.3rem' : '1.6rem'};
      }
    }
  `;

  const { accessToken, environment } = useContext(AccessContext);
  const { pickedOption, setPickedOption } = useContext(RetryContext);

  if (!option) {
    return null;
  }

  const routingReceiptData = get(option, ['routingReceiptData']);
  const shipmentPlans = get(option, ['_links', 'shipmentPlan']);
  const problem = get(option, ['problem']);
  const productConfiguraton = get(option, ['_links', 'productConfiguration']);
  const fulfillmentConfiguration = get(option, ['_links', 'fulfillmentConfiguration', 'href']) as string | undefined;
  const isPickedOption =
    fulfillmentConfiguration === pickedOption?.chosenFCUrl &&
    productConfiguraton?.href === pickedOption?.chosenProductUrl;

  return (
    <Disclosure
      title={option.referenceId}
      key={`${option.optionId}-accordion`}
      data-testid="fulfillment-option-accordion"
      iconStart={<Rank option={option} halfWeight={halfWeight} />}
      iconEnd={<FulfillerAndPrice option={option} halfWeight={halfWeight} />}
      actions={<FulfillmentOptionHeaderActions />}
      marginBottom={8}
    >
      {problem && problem.causes && problem.causes.length > 0 && (
        <Callout tone="critical" title={problem.causes.length === 1 ? 'Reason' : 'Reasons'} isDismissible={false}>
          <FormattedAlertMessages messages={problem.causes} />
        </Callout>
      )}
      <div css={fulfillmentOptionBodyStyles}>
        <div css={productInfoGridStyles} data-testid="option-detail-grid">
          <div css={headlineGrid}>
            <Text as="h3">Details</Text>
            {isItemSuspendedInRouting && fulfillmentConfiguration && productConfiguraton && (
              <Button
                variant={isPickedOption ? 'secondary' : 'primary'}
                UNSAFE_className="pickMeButton"
                onPress={() =>
                  setPickedOption({
                    chosenFCUrl: fulfillmentConfiguration,
                    chosenProductUrl: productConfiguraton.href,
                  })
                }
              >
                {isPickedOption ? 'Picked!' : 'Pick me!'}
              </Button>
            )}
          </div>
          {option._uiLinks?.productManager && (
            <>
              <div css={headerStyles}>Version</div>
              <div>
                <Link href={option._uiLinks.productManager.href} target="_blank" rel="noreferrer">
                  {option._uiLinks.productManager.version}
                </Link>
              </div>
            </>
          )}
          {option.productAttribute && (
            <>
              <div css={headerStyles}>Attributes</div>
              <div>{option.productAttribute}</div>
            </>
          )}
          {productConfiguraton && (
            <>
              <div css={headerStyles}>Product Configuration</div>
              <div>
                <Link href={productConfiguraton.href} target="_blank" rel="noreferrer">
                  View Configuration
                </Link>
              </div>
            </>
          )}
          <>
            <div css={headerStyles}>Fulfillment Configuration</div>
            {option._uiLinks?.fulfillmentConfigurationLink ? (
              <div>
                <a
                  href={option._uiLinks.fulfillmentConfigurationLink.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="fulfillment-config-link"
                >
                  {option.fulfillerName || option.fulfillerId} (Version{' '}
                  {option._uiLinks.fulfillmentConfigurationLink.version})
                </a>
              </div>
            ) : (
              <div data-testid="no-fulfillment-config-link">{option.fulfillerName || option.fulfillerId}</div>
            )}
          </>
        </div>
      </div>
      {shipmentPlans && shipmentPlans.length > 0 ? (
        <ShipmentPlanViewer
          accessToken={accessToken}
          environment={environment}
          shipmentPlanUrl={shipmentPlans[0].href}
          showBasicInfo={false}
          showCondensedView={true}
          data-testid="shipment-planner-component"
        />
      ) : (
        <div css={noShipmentPlanStyles} data-testid="no-shipment-plan">
          <Text as="h3">Shipment Plan</Text>
          <Text as="p">No shipment plan available</Text>
        </div>
      )}
      {routingReceiptData && (
        <div css={noShipmentPlanStyles}>
          <Text as="h3">Routing Insights</Text>
          <RoutingReceiptSummary
            routingReceiptData={routingReceiptData}
            isPickedOption={'rank' in option && option.rank === '1'}
            option={option}
          />
        </div>
      )}
    </Disclosure>
  );
};
